import { useEffect, useContext } from "react";
import { useSelector } from "react-redux";
import { BulkEditViewContext } from "views/Athlete/BulkEditView"
import { AppIsInTestEnvironment } from "functions/environmentUtils";
import { IsExerciseSetUnitSeconds } from "functions/exerciseUtils";

export default function FutureWorkoutPhaseComponent({
    weekOfTheYearKey,
    workoutTemplateKey,
    phaseIndex,
    phase,
    slotSpecificVolumeData,
}) {
    const { 
        userType,
        organizationId,
        ShowProgressionInfoModal, 
        OnStartOverrideDrag, 
        OnOverrideDragEntersCell, 
        GetProgramTableDisplayData, 
        GetPhaseCellStyle, 
        GetSlotCellStyle 
    } = useContext(BulkEditViewContext)

    const setsDisplayData = GetProgramTableDisplayData(weekOfTheYearKey, workoutTemplateKey, 'one-off-sets-override', phaseIndex)

    const isDragging = useSelector(state => state.trainingChart.draggedSlots.isDragging);

    const OnSlotMouseDown = (e, slotIndex, exercise) => {
        if (e.shiftKey) return;
        OnStartOverrideDrag(e, 'slot', weekOfTheYearKey, workoutTemplateKey, phaseIndex, slotSpecificVolumeData, slotIndex, exercise)
    }
    
    const OnSlotMouseEnter = (e, slotIndex, exercise) => {
        if (e.shiftKey) return;
        OnOverrideDragEntersCell(e, 'slot', weekOfTheYearKey, workoutTemplateKey, phaseIndex, slotSpecificVolumeData, slotIndex, exercise)
    }

    const OnSlotShiftClicked = (e, slotIndex) => {
        if (
            e.shiftKey
            && (
                AppIsInTestEnvironment()
                || organizationId === 1 // Only show progression info modal for Kinesis internal org
                || userType === 'super-admin' // Only show progression info modal for super admins
            )
        ) {
            ShowProgressionInfoModal(workoutTemplateKey, phase.name, phaseIndex, slotIndex)
        }
    }

    // Disable text selection when dragging
    useEffect(() => {
        if (isDragging) { document.body.style.userSelect = 'none'; } 
        else { document.body.style.userSelect = ''; }
        return () => { document.body.style.userSelect = ''; };
    }, [isDragging]);

    return (
        <div>

            {/* one-off-sets-override */}

            <div 
                className={GetPhaseCellStyle(weekOfTheYearKey, workoutTemplateKey, phaseIndex)}
                onMouseDown={(e) => OnStartOverrideDrag(e, 'phase', weekOfTheYearKey, workoutTemplateKey, phaseIndex, slotSpecificVolumeData, 0, null)}
                onMouseEnter={(e) => OnOverrideDragEntersCell(e, 'phase', weekOfTheYearKey, workoutTemplateKey, phaseIndex, slotSpecificVolumeData, 0, null)}
            >
                <div className="text-xs flex-grow">{phase.name}</div>

                <div className="text-xs text-gray-600">
                    {setsDisplayData.value || 0} set{setsDisplayData.value === 1 ? '' : 's'}
                </div>
            </div>

            <div className="divide-y divide-gray-400">
                {phase.slots.map((exercise, slotIndex) => (
                    <div
                        key={slotIndex}
                        className={GetSlotCellStyle(weekOfTheYearKey, workoutTemplateKey, phaseIndex, slotIndex)}
                        onMouseDown={(e) => OnSlotMouseDown(e, slotIndex, exercise)}
                        onMouseEnter={(e) => OnSlotMouseEnter(e, slotIndex, exercise)}
                        onClick={(e) => OnSlotShiftClicked(e, slotIndex)}
                    >
                        <div className="my-auto p-2 space-y-1 text-center">

                            {/* one-off-exercise-swap */}

                            <div className='text-xs'>
                                {GetProgramTableDisplayData(weekOfTheYearKey, workoutTemplateKey, 'one-off-exercise-swap', phaseIndex, slotIndex)?.value?.name}
                            </div>

                            {/* one-off-reps-override */}
                            
                            {(() => {

                                const exerciseDisplayData = GetProgramTableDisplayData(weekOfTheYearKey, workoutTemplateKey, 'one-off-exercise-swap', phaseIndex, slotIndex);                                
                                const isSeconds = IsExerciseSetUnitSeconds(exerciseDisplayData?.value?.id);
                                const lateral = exerciseDisplayData?.value?.lateral;
                                
                                const repsDisplayData = GetProgramTableDisplayData(weekOfTheYearKey, workoutTemplateKey, 'one-off-reps-override', phaseIndex, slotIndex);
                                const repsValue = repsDisplayData?.value;

                                if (!exercise || !repsValue){ 
                                    return null;
                                }

                                if (isSeconds === false) {
                                    if (lateral === 'unilateral') {
                                        return <div className="text-xs">{repsValue + ' reps / side'}</div>;
                                    }

                                    if (lateral === 'bilateral') {
                                        return <div className="text-xs">{repsValue + ' reps'}</div>;
                                    }
                                }

                                if (isSeconds === true) {
                                    if (lateral === 'unilateral') {
                                        return <div className="text-xs">{repsValue + ' seconds / side'}</div>;
                                    }

                                    if (lateral === 'bilateral') {
                                        return <div className="text-xs">{repsValue + ' seconds'}</div>;
                                    }
                                }

                                return null;

                            })()}

                            {/* one-off-weight-recommendation-override */}

                            {GetProgramTableDisplayData(weekOfTheYearKey, workoutTemplateKey, 'one-off-weight-recommendation-override', phaseIndex, slotIndex)?.overrideApplied && 
                                <div className="text-xs">
                                    {GetProgramTableDisplayData(weekOfTheYearKey, workoutTemplateKey, 'one-off-weight-recommendation-override', phaseIndex, slotIndex)?.value + ' lbs'}
                                </div>
                            }

                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

// Injury tooltip next to cell className
// ${injuryData(phaseIndex, slotIndex, exercise)?.injuryApplied ? 'bg-red-50 shadow-lg border-red-200' : ''}
// data-tooltip-id={`tooltip-${phaseIndex}-${slotIndex}`}
// data-tooltip-html={`
//     <div class="text-left">
//         <div class="font-bold mb-1 text-orange-900">${exercise.name}</div>
//         ${exercise.progressionLevel ?
//     `<div class="text-sm mb-1 text-orange-800">Level: ${GetProgressionLevelName(exercise.progressionLevel)}</div>`
//     : ''}
//         ${injuryData(phaseIndex, slotIndex, exercise)?.injuryApplied ? `
//             <div class="mt-2 pt-2 border-t border-red-200">
//                 <div class="font-semibold text-red-600 mb-1">Injury Details:</div>
//                 ${Object.entries(injuryData(phaseIndex, slotIndex, exercise).injuryMetadata).map(([key, value]) =>
//         `<div class="text-sm text-red-800">
//                         <span class="font-medium">${key}:</span> ${value}
//                     </div>`
//     ).join('')}
//             </div>
//             `: ''}
//     </div>
// `}